/**
 * Regular-polygon construction geometry (#928). Pure math — no paper.js — so
 * the vertex builders are unit-testable and shared by every tool with a
 * polygon kind (Construction, leather Shape, Cutout, Stitch).
 *
 * Two gestures produce a regular n-gon:
 *  - SIDE mode (default): the drag vector A→B is the polygon's first SIDE;
 *    the remaining vertices unfold to the left of A→B (drag the other way to
 *    flip which side the body lands on).
 *  - CENTER mode (centre snap / Alt): the drag start is the polygon's CENTRE
 *    and the drag vector is a circumscribed RADIUS to the first vertex.
 */

export type Vec2 = { x: number; y: number };

export const MIN_POLYGON_SIDES = 3;
/** Ceiling for the Sides FIELD (panel-enforced). */
export const MAX_POLYGON_SIDES = 60;
/** Ceiling for the AUTO-derived count (#931) — high enough that the visible
 *  view is effectively the only cap the user meets; a 360-gon is a circle for
 *  all practical purposes. */
export const MAX_AUTO_POLYGON_SIDES = 360;
export const DEFAULT_POLYGON_SIDES = 6;

/** Clamp a sides input to a sane regular-polygon range (#928). `null` /
 *  `undefined` (the Sides field cleared) falls back to the default. */
export function clampPolygonSides(n: number | null | undefined): number {
  if (n == null || !Number.isFinite(n)) return DEFAULT_POLYGON_SIDES;
  return Math.min(
    MAX_AUTO_POLYGON_SIDES,
    Math.max(MIN_POLYGON_SIDES, Math.round(n)),
  );
}

/** Circumscribed radius of a regular n-gon with side length `side`. */
export function circumradiusForSide(side: number, sides: number): number {
  const n = clampPolygonSides(sides);
  return side / (2 * Math.sin(Math.PI / n));
}

/** Side length of a regular n-gon with circumscribed radius `radius`. */
export function sideForCircumradius(radius: number, sides: number): number {
  const n = clampPolygonSides(sides);
  return 2 * radius * Math.sin(Math.PI / n);
}

/**
 * Vertices of a regular n-gon whose CENTRE is `center` and whose first vertex
 * sits at `first` (so |first - center| is the circumscribed radius and the
 * polygon's orientation follows the drag). Y-down screen space; vertices wind
 * clockwise on screen. Returns [] for a degenerate radius.
 */
export function regularPolygonFromCenter(
  center: Vec2,
  first: Vec2,
  sides: number,
): Vec2[] {
  const n = clampPolygonSides(sides);
  const dx = first.x - center.x;
  const dy = first.y - center.y;
  const r = Math.hypot(dx, dy);
  if (r < 1e-9) return [];
  const start = Math.atan2(dy, dx);
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const a = start + (2 * Math.PI * i) / n;
    out.push({ x: center.x + r * Math.cos(a), y: center.y + r * Math.sin(a) });
  }
  return out;
}

/**
 * Vertices of a regular n-gon built on the segment A→B as its first SIDE.
 * The polygon body unfolds to the LEFT of the A→B direction in y-down screen
 * space (visually: drag rightwards → the polygon rises above the segment);
 * dragging the opposite way flips it. Returns [] for a degenerate side.
 */
export function regularPolygonFromSide(
  a: Vec2,
  b: Vec2,
  sides: number,
): Vec2[] {
  const n = clampPolygonSides(sides);
  const side = Math.hypot(b.x - a.x, b.y - a.y);
  if (side < 1e-9) return [];
  // Interior angle = (n-2)π/n; the exterior turn between successive edges is
  // 2π/n. Walking A→B then turning LEFT by the exterior angle each step
  // traces the polygon counter-clockwise in math coords (clockwise on the
  // y-down screen), keeping the body left of A→B.
  const turn = -(2 * Math.PI) / n; // y-down: negative = left turn on screen
  let angle = Math.atan2(b.y - a.y, b.x - a.x);
  const out: Vec2[] = [{ x: a.x, y: a.y }];
  let cur = { x: a.x, y: a.y };
  for (let i = 0; i < n - 1; i++) {
    cur = {
      x: cur.x + side * Math.cos(angle),
      y: cur.y + side * Math.sin(angle),
    };
    out.push(cur);
    angle += turn;
  }
  return out;
}

/**
 * Freeform-polygon SIZE constraint (#928): snap the pending segment's end so
 * its length equals `sideLength` (the first drawn side), keeping the cursor's
 * direction. Returns `end` unchanged while the direction is degenerate.
 */
export function snapSegmentToLength(
  from: Vec2,
  end: Vec2,
  sideLength: number,
): Vec2 {
  const dx = end.x - from.x;
  const dy = end.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9 || sideLength <= 0) return end;
  const s = sideLength / len;
  return { x: from.x + dx * s, y: from.y + dy * s };
}

/**
 * Freeform-polygon ANGLE constraint (#928): make every interior angle repeat
 * the polygon's first turn. Given the previous edge `prev→from` and the
 * cursor `end`, snap the new segment's DIRECTION to the previous edge's
 * direction rotated by ±`turnRad` (the sign closest to the cursor), keeping
 * the cursor's length. With no committed turn yet, pass the turn measured
 * from the draft's first two edges.
 */
export function snapSegmentToTurn(
  prev: Vec2,
  from: Vec2,
  end: Vec2,
  turnRad: number,
): Vec2 {
  const inAngle = Math.atan2(from.y - prev.y, from.x - prev.x);
  const len = Math.hypot(end.x - from.x, end.y - from.y);
  if (len < 1e-9) return end;
  const want = Math.atan2(end.y - from.y, end.x - from.x);
  const candA = inAngle + Math.abs(turnRad);
  const candB = inAngle - Math.abs(turnRad);
  const diff = (a: number, b: number) => {
    let d = a - b;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    return Math.abs(d);
  };
  const angle = diff(candA, want) <= diff(candB, want) ? candA : candB;
  return {
    x: from.x + len * Math.cos(angle),
    y: from.y + len * Math.sin(angle),
  };
}

/**
 * Resolve the vertices of an in-flight regular-polygon drag (#928), shared by
 * every tool with a polygon kind. `fromCenter` picks the centre+radius
 * variant; `radius` (circumscribed, when set) pins the size so the drag only
 * aims the polygon; `snapAngle` (when supplied) constrains the drag vector's
 * direction first (the caller passes its angle-snapped cursor). Returns []
 * while the drag is degenerate.
 */
export function resolveRegularPolygonDrag(opts: {
  start: Vec2;
  cursor: Vec2;
  sides: number | null;
  radius: number | null;
  /** Exact side length (mm, #934) — pins the size; wins over `radius`. */
  sideLength?: number | null;
  fromCenter: boolean;
}): Vec2[] {
  const { start, fromCenter } = opts;
  const n = clampPolygonSides(opts.sides);
  let end = { x: opts.cursor.x, y: opts.cursor.y };
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.hypot(dx, dy);
  const sideLen =
    opts.sideLength != null && opts.sideLength > 0 ? opts.sideLength : null;
  const pinned = sideLen != null || (opts.radius != null && opts.radius > 0);
  // Target drag length: a pinned side length (or radius) fixes the size —
  // side length converts to the radius in centre mode and vice versa in side
  // mode; otherwise the cursor distance is the size.
  const target = pinned
    ? fromCenter
      ? sideLen != null
        ? circumradiusForSide(sideLen, n)
        : opts.radius!
      : (sideLen ?? sideForCircumradius(opts.radius!, n))
    : len;
  if (target < 0.5) return [];
  if (len > 1e-9) {
    end = {
      x: start.x + (dx / len) * target,
      y: start.y + (dy / len) * target,
    };
  } else if (pinned) {
    // Click without a drag but a pinned radius — default to pointing east.
    end = { x: start.x + target, y: start.y };
  } else {
    return [];
  }
  return fromCenter
    ? regularPolygonFromCenter(start, end, n)
    : regularPolygonFromSide(start, end, n);
}

/** The signed turn (exterior angle) at vertex `b` walking a→b→c, in radians,
 *  folded to (-π, π]. Zero for degenerate edges. */
export function turnAt(a: Vec2, b: Vec2, c: Vec2): number {
  const in1 = Math.hypot(b.x - a.x, b.y - a.y);
  const out1 = Math.hypot(c.x - b.x, c.y - b.y);
  if (in1 < 1e-9 || out1 < 1e-9) return 0;
  let t = Math.atan2(c.y - b.y, c.x - b.x) - Math.atan2(b.y - a.y, b.x - a.x);
  while (t > Math.PI) t -= 2 * Math.PI;
  while (t <= -Math.PI) t += 2 * Math.PI;
  return t;
}

/**
 * Auto-n regular polygon (#931): given the fixed first SIDE A→B and the
 * cursor, derive how many sides of that length fit into the circumscribed
 * circle whose DIAMETER is the distance from the side's midpoint to the
 * cursor (n from L = D·sin(π/n)), and build the polygon on A→B unfolding
 * toward the cursor's side of the segment. `prevSides` (the previous frame's
 * n) gets hysteresis — the count only switches once the raw value is clearly
 * past the rounding boundary — so the preview doesn't flicker between two
 * counts at the threshold. Returns null while the side is degenerate.
 */
export function autoPolygonFromSide(
  a: Vec2,
  b: Vec2,
  cursor: Vec2,
  prevSides: number | null,
  /** Cap on the circumscribed circle's diameter (project mm) — callers pass
   *  the visible view's size so a near-collinear cursor can't explode the
   *  polygon off-screen. */
  maxDiameter?: number,
): {
  sides: number;
  vertices: Vec2[];
  circle: { x: number; y: number; r: number };
} | null {
  const L = Math.hypot(b.x - a.x, b.y - a.y);
  if (L < 1e-6) return null;
  // The cursor is a POINT ON the circle: the circumscribed circle passes
  // through A, B, and the cursor (three points define it), so a small
  // sideways move already widens the circle — no need to drag out a full
  // diameter. Collinear cursor → infinite circle → the count maxes out.
  const d2 =
    2 *
    (a.x * (b.y - cursor.y) + b.x * (cursor.y - a.y) + cursor.x * (a.y - b.y));
  let D: number;
  let center: Vec2;
  if (Math.abs(d2) < 1e-9) {
    D = Number.POSITIVE_INFINITY;
    center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  } else {
    const aa = a.x * a.x + a.y * a.y;
    const bb = b.x * b.x + b.y * b.y;
    const pp = cursor.x * cursor.x + cursor.y * cursor.y;
    center = {
      x:
        (aa * (b.y - cursor.y) + bb * (cursor.y - a.y) + pp * (a.y - b.y)) / d2,
      y:
        (aa * (cursor.x - b.x) + bb * (a.x - cursor.x) + pp * (b.x - a.x)) / d2,
    };
    D = 2 * Math.hypot(a.x - center.x, a.y - center.y);
  }
  // Never let the circle outgrow what the polygon can follow: past the
  // MAX-sided polygon's circumdiameter for this side length the count can't
  // increase, so growing the circle further just detaches it from the
  // preview (#931).
  const maxNDiameter = L / Math.sin(Math.PI / MAX_AUTO_POLYGON_SIDES);
  const cap = Math.min(
    maxNDiameter,
    maxDiameter != null && maxDiameter > 0 ? maxDiameter : Infinity,
  );
  if (D > cap) {
    // Capped: the circle can no longer pass through the cursor — rebuild it
    // through A and B at the capped diameter, on the cursor's side.
    D = Math.max(cap, L);
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const h = Math.sqrt(Math.max(0, (D / 2) * (D / 2) - (L / 2) * (L / 2)));
    let nx = -(b.y - a.y) / L;
    let ny = (b.x - a.x) / L;
    if ((cursor.x - mid.x) * nx + (cursor.y - mid.y) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    center = { x: mid.x + nx * h, y: mid.y + ny * h };
  }
  // n from the circumscribed circle: L = D·sin(π/n) → n = π / asin(L/D).
  // A diameter at (or below) the side length can't fit a polygon — floor at
  // the triangle.
  let sides: number;
  if (D <= L) {
    sides = MIN_POLYGON_SIDES;
  } else if (!Number.isFinite(D)) {
    sides = prevSides ?? MAX_AUTO_POLYGON_SIDES;
  } else {
    const raw = Math.PI / Math.asin(L / D);
    // Hysteresis: stick with the previous count until the raw value is 0.6
    // past it, so the boundary between n and n+1 doesn't flicker.
    if (prevSides != null && Math.abs(raw - prevSides) < 0.6) {
      sides = prevSides;
    } else {
      sides = Math.round(raw);
    }
    sides = Math.min(
      MAX_AUTO_POLYGON_SIDES,
      Math.max(MIN_POLYGON_SIDES, sides),
    );
  }
  // Unfold the body toward the cursor: regularPolygonFromSide unfolds LEFT of
  // its direction (y-down), so when the cursor sits on the RIGHT of A→B walk
  // the side the other way.
  // Unfold the body toward the CIRCLE's side, not the cursor's: with the
  // cursor close to the segment (between the endpoints, on the minor arc)
  // the through-the-cursor circle bulges to the opposite side, and the
  // polygon must inscribe where the circle body actually is. Fall back to
  // the cursor's side when the centre is degenerate (collinear).
  const refPt =
    Math.hypot(center.x - (a.x + b.x) / 2, center.y - (a.y + b.y) / 2) > 1e-9
      ? center
      : cursor;
  const cross = (b.x - a.x) * (refPt.y - a.y) - (b.y - a.y) * (refPt.x - a.x);
  const vertices =
    cross > 0
      ? regularPolygonFromSide(b, a, sides)
      : regularPolygonFromSide(a, b, sides);
  if (vertices.length < 3) return null;
  // Report the polygon's ACTUAL circumcircle when the input one is
  // degenerate; otherwise the smooth through-the-cursor circle.
  const circle = Number.isFinite(D)
    ? { x: center.x, y: center.y, r: D / 2 }
    : (() => {
        const cx = vertices.reduce((acc, v) => acc + v.x, 0) / vertices.length;
        const cy = vertices.reduce((acc, v) => acc + v.y, 0) / vertices.length;
        return {
          x: cx,
          y: cy,
          r: Math.hypot(vertices[0]!.x - cx, vertices[0]!.y - cy),
        };
      })();
  return { sides, vertices, circle };
}

/**
 * Variable-count polygon on a PINNED circle (#931): the circumscribed
 * diameter is fixed (the Radius preset) and the drag a→cursor draws the
 * first side — its length picks how many such sides fit the circle
 * (n = π/asin(L/D), hysteresis like the free variant). The committed side
 * snaps to the exact chord for the integer count (the pinned circle wins).
 * Returns null while the drag is degenerate.
 */
/**
 * Variable-count polygon on a CENTRED pinned circle (#934, centre mode): the
 * circle is already fixed by its centre and first vertex (the released
 * radius drag, or the pinned Radius preset), and `chord` — the cursor's
 * distance from the first vertex, or a pinned Side length — picks how many
 * sides of that length fit (n = π/asin(L/D), hysteresis like the side
 * variants). The ring is rebuilt FROM THE CENTRE, so it stays inscribed in
 * the fixed circle exactly, anchored at the first vertex. A degenerate chord
 * maxes the count (the polygon hugs its circle); a chord past the inscribed
 * triangle's clamps to it. Returns null while the radius is degenerate.
 */
export function autoPolygonOnCenteredCircle(
  center: Vec2,
  first: Vec2,
  chord: number,
  prevSides: number | null,
): { sides: number; vertices: Vec2[]; radius: number } | null {
  const R = Math.hypot(first.x - center.x, first.y - center.y);
  if (R < 1e-9) return null;
  const D = 2 * R;
  // Longest chord that still closes a polygon (n = 3): D·sin(π/3).
  const maxL = D * Math.sin(Math.PI / MIN_POLYGON_SIDES);
  const L = Math.min(Math.max(chord, 0), maxL);
  let sides: number;
  if (L < 1e-9) {
    sides = prevSides ?? MAX_AUTO_POLYGON_SIDES;
  } else {
    const raw = Math.PI / Math.asin(L / D);
    if (prevSides != null && Math.abs(raw - prevSides) < 0.6) {
      sides = prevSides;
    } else {
      sides = Math.round(raw);
    }
  }
  sides = Math.min(MAX_AUTO_POLYGON_SIDES, Math.max(MIN_POLYGON_SIDES, sides));
  const vertices = regularPolygonFromCenter(center, first, sides);
  return vertices.length >= 3 ? { sides, vertices, radius: R } : null;
}

export function autoPolygonOnPinnedCircle(
  a: Vec2,
  cursor: Vec2,
  diameter: number,
  prevSides: number | null,
): { sides: number; vertices: Vec2[] } | null {
  const D = diameter;
  if (!(D > 0)) return null;
  const dx = cursor.x - a.x;
  const dy = cursor.y - a.y;
  const drag = Math.hypot(dx, dy);
  if (drag < 1e-6) return null;
  // Longest chord that still closes a polygon (n = 3): D·sin(π/3).
  const maxL = D * Math.sin(Math.PI / MIN_POLYGON_SIDES);
  const L = Math.min(drag, maxL);
  const raw = Math.PI / Math.asin(L / D);
  let sides: number;
  if (prevSides != null && Math.abs(raw - prevSides) < 0.6) {
    sides = prevSides;
  } else {
    sides = Math.round(raw);
  }
  sides = Math.min(MAX_AUTO_POLYGON_SIDES, Math.max(MIN_POLYGON_SIDES, sides));
  // Snap the side to the integer count's exact chord on the pinned circle.
  const Ln = D * Math.sin(Math.PI / sides);
  const b = { x: a.x + (dx / drag) * Ln, y: a.y + (dy / drag) * Ln };
  const vertices = regularPolygonFromSide(a, b, sides);
  return vertices.length >= 3 ? { sides, vertices } : null;
}
